// ============================================================
// IndicatorMenu — TradingView-artige Indikator-Liste (Block F).
// Jeder aktive Indikator als Zeile: Name · Auge (ein/aus) · Zahnrad
// (Einstellungen nur dieses Indikators) · X (entfernen).
// „+ Indikator" öffnet einen Picker mit den noch nicht aktiven Indikatoren.
// ============================================================
import React, { useState } from 'react';

export interface Settings {
  tpo: boolean;
  mode: 'letter' | 'profile';
  bracketMin: number;
  valueAreaPct: number;
  rowSizeOverride: number | null;
  ib: boolean;
  singles: boolean;
  excess: boolean;
  prevLevels: boolean;
  devLevels: boolean;
  atrPeriod: number;
  atrLines: boolean;
  atrFactor: number;
  posQty: number;            // Block C: Menge für die P&L-Rechnung des Long/Short-Tools
  indicators: string[];      // Block F: welche Indikatoren in der Liste sind (Reihenfolge)
}

// Katalog aller bekannten Indikatoren. `vis` = Settings-Flag fürs Auge
// (undefined → Werkzeug ohne Sichtbarkeits-Toggle). `cfg` = hat Settings-Dialog.
interface IndDef { key: string; name: string; vis?: keyof Settings; cfg: boolean; }
const CATALOG: IndDef[] = [
  { key: 'tpo',        name: 'TPO-Profil',              vis: 'tpo',        cfg: true },
  { key: 'ib',         name: 'Initial Balance',         vis: 'ib',         cfg: false },
  { key: 'singles',    name: 'Single Prints',           vis: 'singles',    cfg: false },
  { key: 'excess',     name: 'ST/BT-Excess-Tails',      vis: 'excess',     cfg: false },
  { key: 'devLevels',  name: 'Developing POC/VA',       vis: 'devLevels',  cfg: false },
  { key: 'prevLevels', name: 'Vortag pVAH/pPOC/pVAL',   vis: 'prevLevels', cfg: false },
  { key: 'atrLines',   name: 'ATR SL/BE-Linien',        vis: 'atrLines',   cfg: true },
  { key: 'posTool',    name: 'Long/Short P&L',          cfg: true },
];
const DEF = (k: string) => CATALOG.find(d => d.key === k);

export const DEFAULT_SETTINGS: Settings = {
  tpo: true, mode: 'letter', bracketMin: 30, valueAreaPct: 70, rowSizeOverride: null,
  ib: true, singles: true, excess: true, prevLevels: true, devLevels: true,
  atrPeriod: 14, atrLines: false, atrFactor: 1.5, posQty: 1,
  indicators: ['tpo', 'ib', 'singles', 'excess', 'devLevels', 'prevLevels', 'atrLines', 'posTool'],
};

interface Props {
  s: Settings;
  onChange: (patch: Partial<Settings>) => void;
  atrValue: number | null;
  autoRow: number;
}

const lbl: React.CSSProperties = { color: '#94a3b8', fontSize: 12 };
const inputCss: React.CSSProperties = { background: '#0a0e1a', color: '#fff', border: '1px solid #1e2539', borderRadius: 6, padding: '5px 8px', fontSize: 12, width: 90 };

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '5px 0' }}>
    <span style={lbl}>{label}</span>{children}
  </div>
);

// kleines Icon-Button-Styling für Auge/Zahnrad/X
const iconBtn = (active = false, danger = false): React.CSSProperties => ({
  background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, lineHeight: 1,
  padding: '4px 5px', borderRadius: 5, opacity: active ? 1 : 0.55,
  color: danger ? '#f87171' : '#cbd5e1',
});

const IndicatorMenu: React.FC<Props> = ({ s, onChange, atrValue, autoRow }) => {
  const [open, setOpen] = useState(false);
  const [picker, setPicker] = useState(false);
  const [gear, setGear] = useState<string | null>(null); // Schlüssel des aktiven Settings-Dialogs

  const list = s.indicators.map(DEF).filter((d): d is IndDef => !!d);
  const available = CATALOG.filter(d => !s.indicators.includes(d.key));

  const toggleVis = (d: IndDef) => { if (d.vis) onChange({ [d.vis]: !s[d.vis] } as Partial<Settings>); };
  const remove = (k: string) => {
    const d = DEF(k);
    const patch: Partial<Settings> = { indicators: s.indicators.filter(x => x !== k) };
    if (d?.vis) (patch as any)[d.vis] = false; // entfernen blendet auch das Overlay aus
    onChange(patch); if (gear === k) setGear(null);
  };
  const add = (d: IndDef) => {
    const patch: Partial<Settings> = { indicators: [...s.indicators, d.key] };
    if (d.vis) (patch as any)[d.vis] = true;
    onChange(patch); setPicker(false);
  };

  // Per-Indikator-Settings-Dialog (nur Inputs dieses einen Indikators).
  const renderGear = (k: string) => {
    if (k === 'tpo') return (
      <>
        <Row label="Modus">
          <div style={{ display: 'flex', gap: 4 }}>
            {(['letter', 'profile'] as const).map(m => (
              <button key={m} onClick={() => onChange({ mode: m })} style={{
                background: s.mode === m ? '#8b5cf6' : '#0a0e1a', color: s.mode === m ? '#fff' : '#8b93a7',
                border: '1px solid #1e2539', borderRadius: 5, padding: '4px 8px', fontSize: 11, cursor: 'pointer',
              }}>{m === 'letter' ? 'Letter' : 'Profil'}</button>
            ))}
          </div>
        </Row>
        <Row label="Bracket (min)">
          <select style={inputCss} value={s.bracketMin} onChange={e => onChange({ bracketMin: +e.target.value })}>
            {[5, 15, 30, 60].map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </Row>
        <Row label="Value Area %">
          <input type="number" min={50} max={90} style={inputCss} value={s.valueAreaPct}
            onChange={e => onChange({ valueAreaPct: Math.min(90, Math.max(50, +e.target.value || 70)) })} />
        </Row>
        <Row label="Row-Size">
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="number" step="any" style={{ ...inputCss, width: 70 }}
              value={s.rowSizeOverride ?? ''} placeholder={autoRow.toFixed(4)}
              onChange={e => onChange({ rowSizeOverride: e.target.value === '' ? null : +e.target.value })} />
            <button onClick={() => onChange({ rowSizeOverride: null })} style={{
              background: s.rowSizeOverride == null ? '#8b5cf6' : '#0a0e1a', color: s.rowSizeOverride == null ? '#fff' : '#8b93a7',
              border: '1px solid #1e2539', borderRadius: 5, padding: '4px 6px', fontSize: 10, cursor: 'pointer',
            }}>Auto</button>
          </div>
        </Row>
      </>
    );
    if (k === 'atrLines') return (
      <>
        <Row label="Periode">
          <input type="number" min={2} style={inputCss} value={s.atrPeriod}
            onChange={e => onChange({ atrPeriod: Math.max(2, +e.target.value || 14) })} />
        </Row>
        <Row label="Faktor">
          <input type="number" step="0.1" min={0.1} style={inputCss} value={s.atrFactor}
            onChange={e => onChange({ atrFactor: Math.max(0.1, +e.target.value || 1.5) })} />
        </Row>
        <Row label="Aktueller ATR">
          <span style={{ color: atrValue != null ? '#22d3ee' : '#475569', fontFamily: 'monospace', fontSize: 12 }}>
            {atrValue != null ? atrValue.toLocaleString('en-US', { maximumFractionDigits: 4 }) : '–'}
          </span>
        </Row>
      </>
    );
    if (k === 'posTool') return (
      <Row label="Menge (für P&L)">
        <input type="number" step="any" min={0} style={inputCss} value={s.posQty}
          onChange={e => onChange({ posQty: Math.max(0, +e.target.value || 0) })} />
      </Row>
    );
    return <div style={{ color: '#5b6678', fontSize: 12, padding: '4px 0' }}>Keine Einstellungen.</div>;
  };

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: open ? '#8b5cf6' : 'transparent', color: open ? '#fff' : '#8b93a7',
        border: '1px solid ' + (open ? '#8b5cf6' : '#1e2539'), borderRadius: 6, padding: '5px 12px',
        fontSize: 11, fontWeight: 700, cursor: 'pointer',
      }}>⚙ Indikatoren</button>

      {open && (
        <>
          <div onClick={() => { setOpen(false); setPicker(false); setGear(null); }} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
          <div style={{
            position: 'absolute', top: 34, right: 0, zIndex: 21, width: 320, maxWidth: 'calc(100vw - 24px)',
            background: '#0c1120', border: '1px solid #1e2539', borderRadius: 10, padding: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,.5)', maxHeight: '80vh', overflowY: 'auto',
          }}>
            <div style={{ color: '#8b5cf6', fontWeight: 700, fontSize: 12, marginBottom: 8, textTransform: 'uppercase' }}>Indikatoren</div>

            {list.length === 0 && <div style={{ color: '#5b6678', fontSize: 12, padding: '6px 0' }}>Keine Indikatoren aktiv.</div>}

            {list.map(d => {
              const visible = d.vis ? !!s[d.vis] : true;
              return (
                <div key={d.key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0' }}>
                    <span style={{ flex: 1, color: visible ? '#e2e8f0' : '#5b6678', fontSize: 12.5 }}>{d.name}</span>
                    {d.vis && (
                      <button onClick={() => toggleVis(d)} title={visible ? 'Ausblenden' : 'Einblenden'} style={iconBtn(visible)}>
                        {visible ? '👁' : '🚫'}
                      </button>
                    )}
                    {d.cfg && (
                      <button onClick={() => setGear(gear === d.key ? null : d.key)} title="Einstellungen" style={iconBtn(gear === d.key)}>⚙</button>
                    )}
                    <button onClick={() => remove(d.key)} title="Entfernen" style={iconBtn(true, true)}>✕</button>
                  </div>
                  {gear === d.key && (
                    <div style={{ background: '#0a0e1a', border: '1px solid #1e2539', borderRadius: 8, padding: '6px 10px', margin: '2px 0 8px' }}>
                      {renderGear(d.key)}
                    </div>
                  )}
                </div>
              );
            })}

            <div style={{ height: 1, background: '#1e2539', margin: '10px 0' }} />
            <button onClick={() => setPicker(p => !p)} disabled={available.length === 0} style={{
              width: '100%', background: picker ? '#8b5cf6' : '#0a0e1a', color: available.length === 0 ? '#475569' : (picker ? '#fff' : '#cbd5e1'),
              border: '1px solid #1e2539', borderRadius: 7, padding: '7px 0', fontSize: 12, fontWeight: 700,
              cursor: available.length === 0 ? 'not-allowed' : 'pointer',
            }}>+ Indikator</button>

            {picker && available.length > 0 && (
              <div style={{ marginTop: 6, border: '1px solid #1e2539', borderRadius: 8, overflow: 'hidden' }}>
                {available.map(d => (
                  <button key={d.key} onClick={() => add(d)} style={{
                    display: 'block', width: '100%', textAlign: 'left', background: 'transparent', color: '#cbd5e1',
                    border: 'none', borderBottom: '1px solid #141a2c', padding: '8px 10px', fontSize: 12, cursor: 'pointer',
                  }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#161d33')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >{d.name}</button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default IndicatorMenu;
